import { useMemo } from "react"
import { t } from "i18next"
import { cloneDeep } from "lodash"

import MultipleChart from "@/app/report/components/chart/pdf/MultipleChart"
import PrintableSection from "@/app/report/components/section/pdf/PrintableSection"
import { VALUE_POSITION_TYPE } from "@/app/report/common/constants"
import {
  ChartRow,
  SectionSessionDetails,
  SessionDetailsData,
} from "@/app/report/common/constructor"
import {
  MAX_CHART_PER_PAGE,
  MAXIMUM_CHART_LEN_BY_BAR_CHART,
  countBarChartAxes,
  getReferenceItemCount,
} from "@/app/report/common/export/constants"
import IntervalChart from "@/app/report/components/chart/pdf/IntervalChart"

export default function PrintableSessionDetailSection({
  data,
  scaleRatio = 1,
  currentDecimalPoint = 1,
  currentChartAnnotation = VALUE_POSITION_TYPE.HIDE_VALUES,
}: {
  data: SectionSessionDetails
  scaleRatio: number
  currentDecimalPoint?: number
  currentChartAnnotation?: VALUE_POSITION_TYPE
}) {
  const sessionList = useMemo(() => {
    if (!data || !data.sessionDetailsData) {
      return []
    }

    return data.sessionDetailsData
      .map((session) => {
        if (!session.dataSession?.length) {
          return undefined
        }

        return session
      })
      .filter((session) => !!session)
  }, [data])

  const splittedSessionList = useMemo(() => {
    if (!sessionList || !sessionList.length) {
      return []
    }

    const { dataSession, ...sessionDetails } = (sessionList as SessionDetailsData[])[0]
    const copied = cloneDeep(dataSession)
    const barChartCount = countBarChartAxes(sessionDetails.settings?.yAxis)
    const splittedSessionList = []
    while (copied.length > 0) {
      splittedSessionList.push(copied.splice(0, MAXIMUM_CHART_LEN_BY_BAR_CHART[barChartCount]))
    }

    const flatten = splittedSessionList.map(
      (data) =>
        new SessionDetailsData({
          ...sessionDetails,
          dataSession: data,
          sessionSelectedRoot: sessionDetails.sessionSelectedRoot,
          sessionSelected: sessionDetails.sessionSelected,
          activitySessionType: sessionDetails.activitySessionType,
        }),
    )
    const splitByPage = []

    while (flatten.length > 0) {
      splitByPage.push(flatten.splice(0, MAX_CHART_PER_PAGE))
    }

    return splitByPage as SessionDetailsData[][]
  }, [sessionList])

  // 세션 차트 조각들이 공유할 기준 항목 개수 (자르기 전 전체 세션 수 기준).
  const sessionReferenceItemCount = useMemo(() => {
    const first = (sessionList as SessionDetailsData[])[0]

    if (!first) {
      return 0
    }

    return getReferenceItemCount(countBarChartAxes(first.settings?.yAxis), first.dataSession.length)
  }, [sessionList])

  // 인터벌 차트 조각들이 공유할 기준 항목 개수. 자르기 전 원본에서 재야 하므로
  // splittedSessionData 가 아니라 data 에서 구한다. 키 구조는 highestValuesFormIntervals 와 동일.
  const intervalReferenceItemCounts = useMemo(() => {
    const result: Record<string, Record<number, number>> = {}

    data?.sessionDetailsData?.forEach((session) => {
      const barChartCount = countBarChartAxes(session.settings?.yAxis)

      session.dataInterval.forEach((interval) => {
        if (!result[interval.name]) {
          result[interval.name] = {}
        }

        result[interval.name][session.interval] = getReferenceItemCount(
          barChartCount,
          interval.data.length,
        )
      })
    })

    return result
  }, [data])

  const splittedSessionData = useMemo(() => {
    if (!data || !data.sessionDetailsData) {
      return []
    }

    return data.sessionDetailsData.map((data) => {
      const copied = cloneDeep(data)
      const barChartCount = countBarChartAxes(copied.settings?.yAxis)
      const splittedDataInterval: SessionDetailsData[] = []
      const copiedInterval = copied.dataInterval.slice()
      copiedInterval.forEach((interval) => {
        const copiedData = interval.data.slice()
        const result: ChartRow[][] = []
        while (copiedData.length > 0) {
          result.push(copiedData.splice(0, MAXIMUM_CHART_LEN_BY_BAR_CHART[barChartCount]))
        }

        result.forEach((items) => {
          const insertItem = {
            ...copied,
            dataInterval: [
              {
                ...interval,
                data: items,
              },
            ],
          }
          splittedDataInterval.push(insertItem)
        })
      })

      const result = []

      while (splittedDataInterval.length > 0) {
        const intervals = splittedDataInterval.splice(0, MAX_CHART_PER_PAGE)

        while (
          intervals.length > 0 &&
          intervals[0].dataInterval[0].name !== intervals.at(-1)?.dataInterval[0].name
        ) {
          splittedDataInterval.unshift(intervals.pop() as SessionDetailsData)
        }
        result.push(intervals)
      }

      return result
    })
  }, [data])

  const highestValuesFormSessions = useMemo(() => {
    const result = [0, 0, 0]

    splittedSessionList.forEach((session) =>
      session
        .map((s) => s.dataSession)
        .reduce((acc, cur) => acc.concat(cur), [])
        .forEach((session) =>
          session.data.forEach((data, idx) => {
            result[idx] = Math.max(result[idx], data || 0)
          }),
        ),
    )

    return result
  }, [splittedSessionList])

  const highestValuesFormIntervals = useMemo(() => {
    const result: Record<string, Record<number, number[]>> = {}

    splittedSessionData.forEach((item) =>
      item
        .reduce((acc, cur) => acc.concat(cur), [])
        .map((item) => ({ interval: item.interval, dataInterval: item.dataInterval[0] }))
        .forEach((item) =>
          item.dataInterval.data.forEach((data) => {
            if (!result[item.dataInterval.name]) {
              result[item.dataInterval.name] = {}
            }

            if (!result[item.dataInterval.name][item.interval]) {
              result[item.dataInterval.name][item.interval] = data.data as number[]
            } else {
              result[item.dataInterval.name][item.interval] = result[item.dataInterval.name][
                item.interval
              ].map((value, idx) => Math.max(value, data.data[idx] || -1))
            }
          }),
        ),
    )

    return result
  }, [splittedSessionData])

  const totalItemCount = useMemo(() => {
    return splittedSessionList.flat().length + splittedSessionData.flat().length
  }, [splittedSessionList, splittedSessionData])

  const getIsSessionSplitted = (
    chart: SessionDetailsData,
    chartList: SessionDetailsData[],
  ): boolean => {
    if (!chart.settings || chartList.length === 1) {
      return false
    }

    const barChartCount = countBarChartAxes(chart.settings?.yAxis)
    const maximumChartLen = MAXIMUM_CHART_LEN_BY_BAR_CHART[barChartCount]

    return chart.dataSession.length !== maximumChartLen
  }

  const getIsIntervalSplitted = (chart: SessionDetailsData, chartList: SessionDetailsData[]) => {
    if (!chart.settings || chartList.length === 1) {
      return false
    }
    const barChartCount = countBarChartAxes(chart.settings?.yAxis)
    const maximumChartLen = MAXIMUM_CHART_LEN_BY_BAR_CHART[barChartCount]

    return chart.dataInterval[0].data.length !== maximumChartLen
  }

  return (
    <div className="renewal-pdf-container SessionDetailsPdfCont">
      {splittedSessionList?.flat().map((chart, index) => {
        return (
          <PrintableSection
            sectionTitle={t("sessionDetails")}
            scaleRatio={scaleRatio}
            currentItemCount={index + 1}
            totalItemCount={totalItemCount}
            key={`section_chart_by_player_${index}`}>
            <MultipleChart
              loading={false}
              chartData={chart}
              splitted={getIsSessionSplitted(chart, splittedSessionList.flat())}
              maxItemCount={sessionReferenceItemCount}
              scaleRatio={scaleRatio}
              decimalPoint={currentDecimalPoint}
              chartAnnotation={currentChartAnnotation}
              highestValues={highestValuesFormSessions}
            />
          </PrintableSection>
        )
      })}

      {/* 모든 interval 데이터 표시 - 각 차트를 개별 섹션으로 분리 */}
      {splittedSessionData?.map((intervalList: SessionDetailsData[][], groupIndex: number) => {
        return intervalList.map((chartList: SessionDetailsData[], listIndex: number) => {
          const currentIndex = splittedSessionList?.flat().length + groupIndex + 1

          return chartList.map((chart: SessionDetailsData, itemIndex: number) => {
            const isFirstInGroup = itemIndex === 0

            return (
              <PrintableSection
                sectionTitle={t("sessionDetails")}
                scaleRatio={scaleRatio}
                currentItemCount={currentIndex}
                totalItemCount={totalItemCount}
                subjectTitle={chart.dataInterval[0].name}
                subjectSubtitle={chart.interval + t("mins")}
                hideHeader={!isFirstInGroup}
                key={`interval-${groupIndex}-${listIndex}-${itemIndex}`}>
                <IntervalChart
                  loading={false}
                  chartData={chart}
                  splitted={getIsIntervalSplitted(chart, chartList)}
                  maxItemCount={
                    intervalReferenceItemCounts[chart.dataInterval[0].name]?.[chart.interval]
                  }
                  scaleRatio={scaleRatio}
                  decimalPoint={currentDecimalPoint}
                  chartAnnotation={currentChartAnnotation}
                  highestValues={
                    highestValuesFormIntervals[chart.dataInterval[0].name][chart.interval]
                  }
                />
              </PrintableSection>
            )
          })
        })
      })}
    </div>
  )
}
